import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Import Link from react-router-dom
import {
  Grid,
  Autocomplete,
  createFilterOptions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { getLeads } from '../API/leadAPI';
import { getSalesReps, getSalesRep } from '../API/salesRepAPI';
import { getCacheClients } from '../API/clientAPI';
import { styled } from "@mui/material/styles";
import BaseWrapper from "../../components/shared/BaseWrapper";
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';


const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

function ManageLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsCache, setClientsCache] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);

  // Client filter configuration
  const clientFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option?.name || '',
  });

  const [searchFilter, setSearchFilter] = useState({
    startDate: '',
    endDate: '',
    client_id: '',
    sales_rep_id: '',
    status: 'open',
  })

  const handleAddNewLead = () => {
    navigate(`/add-new-lead`)
  }

  const handleViewClick = (lead) => {
    navigate(`/view-lead`, { state: { lead } });
  };

  const handleEditClick = (lead) => {
    navigate(`/edit-lead`, { state: { lead } });
  };

  useEffect(() => {
    // Fetch sales representatives and store them in a dictionary for easy lookup
    const fetchSalesReps = async () => {
      try {
        const reps = await getSalesReps();
        setSalesReps(reps);
      } catch (err) {
        console.error("Failed to fetch sales representatives", err);
      }
    };

    fetchSalesReps();
  }, []);

  useEffect(() => {
    const fetchCacheClients = async () => {
      try {
        setClientsLoading(true);
        // Fetch clients
        const raw = await getCacheClients();
        // normalize + de-dupe by ID (keeps same-name/different-ID entries)
        const cleaned = (Array.isArray(raw) ? raw : []).map(c => ({
          ...c,
          name: c?.name ? c.name.trim() : c?.name,
        }));
        const byId = new Map();
        for (const c of cleaned) {
          if (c?.id && !byId.has(c.id)) byId.set(c.id, c);
        }
        const unique = Array.from(byId.values())
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setClientsCache(unique);
        setLoading(false);

      } catch (err) {
        setError(err.message);
        setLoading(false);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchCacheClients();
  }, []);

  const handleSearchClient = (event, value) => {
    setSelectedClient(value || null);
    setSearchFilter((prev) => ({
      ...prev,
      client_id: value ? value.id : null,
    }));
  };


  const handleSearchSalesRep = (event, value) => {
    setSelectedSalesRep(value || null); // Update the selected sales rep

    // Update searchFilter with sales_rep_id or reset if null
    setSearchFilter((prev) => ({
      ...prev,
      sales_rep_id: value ? value.id : '',  // Update sales_rep_id in the filter
    }));

  };


  const handleSearchButtonClick = async () => {
    console.log(searchFilter.client_id)
    const leadResults = await getLeads(searchFilter.client_id, searchFilter.sales_rep_id, searchFilter.status, searchFilter.startDate, searchFilter.endDate);

    leadResults.forEach((lead) => {
      const matchingRep = salesReps.find(rep => rep.id === lead.sales_rep_id);
      if (matchingRep) {
        lead.sales_rep = {
          firstname: matchingRep.firstname,
          lastname: matchingRep.lastname
        };
      } else {
        lead.sales_rep = {
          firstname: '',
          lastname: ''
        };
      }
    });

    setLeads(leadResults);
  };

  const handleStatusChange = (event) => {
    setSearchFilter((prev) => ({
      ...prev,
      status: event.target.value,
    }));
  };



  return (


    <BaseWrapper
      titleIcon={<TrackChangesIcon />}
      title="Sales"
      subTitle="Manage Leads"
      subTitleRoute={"/manage-leads"}
      createButtonLabel="Add New Lead"
      onClickCreateButton={() => navigate("/add-new-lead")}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >

      <Grid item xs={12}>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3} md={3}>
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={searchFilter.startDate}
              onChange={(e) =>
                setSearchFilter((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <TextField
              label="End Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={searchFilter.endDate}
              onChange={(e) =>
                setSearchFilter((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
            />
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Autocomplete
              options={clientsCache}
              getOptionLabel={(client) => client?.name || ""} // Fallback to an empty string
              value={selectedClient || null} 
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              filterOptions={(options, params) => clientFilter(options, params)}
              openOnFocus
              autoHighlight
              onChange={(event, newValue) =>
                handleSearchClient(event, newValue)
              } // Updates selected value
              loading={clientsLoading}
              noOptionsText="No clients found"
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{option.name}</span>
                    <small style={{ opacity: 0.7 }}>ID: {option.id}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Name"
                  variant="outlined"
                  fullWidth
                  error={!!error} // Displays error styling if there is an error
                  helperText={error || ""} // Displays error message
                />
              )}
            />
          </Grid>
          <Grid item xs={4} sm={4} md={4}>
            <Autocomplete
              options={salesReps}
              getOptionLabel={(salesRep) =>
                salesRep && salesRep.firstname && salesRep.lastname
                  ? `${salesRep.firstname} ${salesRep.lastname}`
                  : ''
              }
              value={selectedSalesRep || null}  // Avoid undefined state
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              openOnFocus
              autoHighlight
              onChange={handleSearchSalesRep}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sales Rep Name"
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error || ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={3} sm={3} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                value={searchFilter.status}
                onChange={handleStatusChange}
                label="Status"
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="ocular">Ocular</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} container alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick} // Call button handler
              disabled={isLoading} // Disable button while loading
            >
              {isLoading ? "Searching..." : "Search"}{" "}
              {/* Change button label dynamically */}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <StyledHr />
      {leads.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><b>Client</b></TableCell>
                <TableCell><b>Sales Rep</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead, index) => (
                <TableRow key={index}>
                  <TableCell>{lead.client.name || ''}</TableCell>
                  <TableCell>
                    {lead.sales_rep ? `${lead.sales_rep.firstname} ${lead.sales_rep.lastname}` : ''}
                  </TableCell>
                  <TableCell>{lead.status || ''}</TableCell>
                  <TableCell>
                    <ButtonGroup
                      variant="outlined"
                      size="small"
                      aria-label="action button group"
                      sx={{
                        gap: 0, // No gap between buttons
                        padding: 0, // Remove extra space around the group if desired
                      }}
                    >
                      <Tooltip title="View" arrow>
                        <Button
                          onClick={() => navigate(`/view-lead/${lead.id}`)}
                          startIcon={<VisibilityIcon />}
                          sx={{
                            textTransform: "none",
                            minWidth: "32px", // Keep buttons compact
                            padding: "6px",
                            borderRadius: "4px",
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <Button
                          onClick={() => navigate(`/edit-lead/${lead.id}`)}
                          startIcon={<EditIcon />}
                          sx={{
                            textTransform: "none",
                            minWidth: "32px", // Keep buttons compact
                            padding: "6px",
                            borderRadius: "4px",
                          }}
                        />
                      </Tooltip>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </BaseWrapper>
  );
}

export default ManageLeads;
